<script lang="ts">
  import {
    type Api_Account,
    type Api_PaymentMethod,
    type Maybe,
    type RecurlyBillingInfoInput
  } from '$lib/types/api/graphql';
  import { getApolloClient, gql, query } from '$lib/apollo';
  import { analytics, uuidv4 } from '$lib/utils';
  import { captureException } from '@sentry/sveltekit';
  import { appearance } from './appearance';

  import Button from '$mds/Buttons/Button/Button.svelte';

  import { TRACK_PRO_PAYMENT_NEW, TRACK_PRO_PAYMENT_SELECT } from '$lib/tracking/pro';
  import { createEventDispatcher, getContext, onMount, tick } from 'svelte';
  import { GET_BILLING_INFO } from './gql';
  import { secureFlow } from './payment';
  import TogglePayWithCredit from './TogglePayWithCredit.svelte';
  import { faCreditCard } from '@fortawesome/pro-solid-svg-icons';
  import { Toasts } from '$lib/stores';
  import type { App } from '$lib/types';
  import {
    STRIPE_PUBLISHABLE_KEY,
    RECURLY_KEY,
    BASE_APP_URL,
    STRIPE_PAYPAL_PM,
    STRIPE_ACCOUNT_ID
  } from '$lib/constants';
  import IconWrapper from '$mds/IconWrapper';
  import {
    faApplePay,
    faCcAmex,
    faCcDiscover,
    faCcJcb,
    faCcMastercard,
    faCcVisa,
    faGooglePay,
    faPaypal
  } from '@fortawesome/free-brands-svg-icons';
  import SectionDivider from '$mds/Dividers/SectionDivider';

  const { irkAccount, featureFlags } = getContext<App>('app');
  const dispatch = createEventDispatcher();
  const uid = uuidv4();

  export let paymentKind: 'PREVIOUS_CARD' | 'NEW_CARD' | 'PAYPAL' | null = null;
  export let unselected = false;
  export let selectedCard: Api_PaymentMethod | null | undefined = null;
  export let paymentGateway;
  export let isPaymentMethodValid = false;
  export let isChangedPaymentMethod = false;
  export let paymentsEnabled = ['PREVIOUS_CARD', 'NEW_CARD', 'PAYPAL'];

  export let cardExpired = false;
  export let expiringSoon = false;
  export let disableExpired = true;
  export let total: number | null = null;

  /** for paying with metafy credit */
  export let allowPayWithCredit = false;
  export let payWithCredit = false;
  export let payWithCreditMessage: string | undefined = undefined;
  export let payWithCreditTooltipContent: string | undefined = undefined;

  export const fetchToken = async () => {
    let token = null;
    if (paymentGateway) {
      token = await paymentGateway.fetchToken();
    }
    return token;
  };

  export const getBillingInfo = async (
    paymentGatewayResponseSecureToken: string | undefined
  ): Promise<{ billingInfo: RecurlyBillingInfoInput } | {}> => {
    let failed = false;
    if (paymentGatewayResponseSecureToken) {
      // 3ds flow
      let threeDSecureResultToken: { token?: string; message?: string } = {};
      try {
        let token = await fetchToken();
        threeDSecureResultToken = await secureFlow(paymentGatewayResponseSecureToken);
        return { billingInfo: { token, secureToken: threeDSecureResultToken.token } };
      } catch (error) {
        failed = true;
        captureException(error);
      }
    } else {
      // non-3ds flow
      // recurly saves the previous card info, so if the user selects that, we don't need to do anything
      if (paymentKind === 'PREVIOUS_CARD') return {};

      try {
        let token = await fetchToken();
        return { billingInfo: { token } };
      } catch (error) {
        failed = true;
      }

      if (failed) {
        Toasts.push({
          kind: 'error',
          content: 'Error in Payment Authorisation, please try again.'
        });
      }
    }
    return {};
  };

  let isAddingNewCard = false;
  let isNewCardValid = false;
  let initialPaymentKind = paymentKind;

  // Gateways
  let recurlyCard;
  let recurlyPaypal;

  // Get saved cards
  $: cardsResponse = query<Api_Account>({
    query: GET_BILLING_INFO,
    fetchPolicy: 'network-only'
  });

  const setDefaultPayment = (savedMethod: Maybe<Api_PaymentMethod> | null, unselected) => {
    // dont set default if option is set for unselected
    if (unselected) return;

    // If there are no saved cards, 'add a new card' is selected by default.
    if (!savedMethod) {
      isAddingNewCard = true;
      paymentKind = 'NEW_CARD';
      initialPaymentKind = 'NEW_CARD';
    } else if (selectedCard === null) {
      isAddingNewCard = false;
      if (savedMethod.type === 'credit_card') {
        paymentKind = 'PREVIOUS_CARD';
        initialPaymentKind = 'PREVIOUS_CARD';
        selectedCard = savedMethod;
        isPaymentMethodValid = true;
        paymentGateway = recurlyCard;
      } else if (savedMethod.type === 'paypal_billing_agreement') {
        paymentKind = 'PAYPAL';
        initialPaymentKind = 'PAYPAL';
        isPaymentMethodValid = true;
        paymentGateway = recurlyPaypal;
      }
      const { country, postalCode } = $cardsResponse.data?.currentSession?.billingInfo ?? {};
      dispatch('details', { country, postalCode });
    }

    dispatch('loaded');
  };

  function handleChangePaymentMethod(paymentKind) {
    dispatch('changePaymentKind', paymentKind);
    trackPaymentTypeChange(paymentKind);
    const { country, postalCode } = $cardsResponse.data?.currentSession?.billingInfo ?? {};
    dispatch('details', { country, postalCode });
  }

  function trackPaymentTypeChange(paymentKind) {
    analytics.track({
      name: TRACK_PRO_PAYMENT_SELECT,
      props: { selected: paymentKind.toLowerCase().replace('_', '') }
    });
    if (paymentKind === 'NEW_CARD') {
      analytics.track({ name: TRACK_PRO_PAYMENT_NEW });
    }
  }

  $: !$cardsResponse.loading &&
    setDefaultPayment($cardsResponse.data?.currentSession?.billingInfo?.paymentMethod, unselected);
  $: isChangedPaymentMethod = paymentKind !== initialPaymentKind || (isAddingNewCard && isNewCardValid);
  $: paymentMethod = $cardsResponse.data?.currentSession?.billingInfo?.paymentMethod;

  // New Billing Flow -- Everything below is related to these changes
  let stripe: any, elements: any, paymentElement: any;
  let stripePaymentElementRendered = false;
  let stripeMethodSelected = false;
  let parentElement: HTMLElement;

  // Setup is used for adding billing information without a purchase, it causes a verification on the card (VOID transaction)
  export let isForSetup = false;
  export let hasPaymentMethodSelected = !!paymentMethod;

  $: savedMethodSelected = !!paymentMethod;
  $: hasPaymentMethodSelected = savedMethodSelected || (postalCodeIsValid && countryIsValid && stripeMethodSelected);
  $: initialCountry = !!paymentMethod
    ? listOfCountries.find(c => c.value === $cardsResponse.data?.currentSession?.billingInfo?.country)
    : $irkAccount?.requestCountry === 'XX'
      ? null
      : listOfCountries.find(c => c.value === $irkAccount?.requestCountry);

  let postalCode: string | undefined;
  let listOfCountries: { label: string; value: string }[] = [];

  $: postalCode = $cardsResponse.data?.currentSession?.billingInfo?.postalCode ?? '';
  $: country = initialCountry?.value ?? '';
  $: countryIsValid = !!(country && country.length === 2);
  $: postalCodeIsValid = !!postalCode;
  $: {
    // This pushes updates to the parent of the country and postalCode
    // This is used to calculate the Tax for showing it in the breakdown of the
    // checkout
    dispatch('details', { country, postalCode });
    // Also update the Stripe Payment Element with the new values so they are
    // prefilled if the user decides to add a new card -- This is just a nice UX
    // so they don't have to enter their details again. Right now this is the only
    // information we collect (but don't store ourselves). Recurly does want First name & Last name
    // and we did collect that in the past, but because we're not using Recurly.js for Card tokenization anymore
    // it should no longer be an actual requirement.
    if (stripePaymentElementRendered && !paymentElement?._destroyed) {
      paymentElement?.update({
        defaultValues: {
          billingDetails: {
            address: {
              country,
              postal_code: postalCode
            }
          }
        }
      });
    }
  }

  export function resetPaymentUI() {
    stripeMethodSelected = false;
    stripePaymentElementRendered = false;
    savedMethodSelected = !!paymentMethod;
    isChangedPaymentMethod = false;
    if (!paymentElement?._destroyed) {
      paymentElement?.unmount('#stripe-payment-element');
      paymentElement?.destroy();
    }
  }

  export function renderStripePaymentElement() {
    if (!window.Stripe) {
      setTimeout(() => {
        if (!window.Stripe) {
          Toasts.push({
            kind: 'error',
            content: 'Our payment processor is not available. You may have an extension blocking Stripe'
          });
        } else {
          renderStripePaymentElement();
        }
      }, 1000);
      return;
    }
    stripe = window.Stripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: STRIPE_ACCOUNT_ID });
    elements = stripe.elements({
      mode: isForSetup ? 'setup' : 'payment',
      currency: 'usd',
      setupFutureUsage: 'off_session',
      captureMethod: 'automatic',
      // This is just a placeholder, it gets updated when executePayment is called
      // and it's for display purposes only on popups for Cash App and others.
      amount: isForSetup ? undefined : 100,
      loader: 'always',
      appearance,
      customPaymentMethods: [
        {
          id: STRIPE_PAYPAL_PM,
          options: {
            type: 'static'
          }
        }
      ]
    });
    paymentElement = elements.create('payment', {
      layout: {
        type: 'tabs',
        defaultCollapsed: true
      },
      defaultValues: {
        billingDetails: {
          email: $irkAccount?.email,
          address: {
            country,
            postal_code: postalCode
          }
        }
      }
    });
    // Recreate the element in the DOM if it is removed
    if (!document.getElementById('stripe-payment-element')) {
      const stripePaymentElement = document.createElement('div');
      stripePaymentElement.id = 'stripe-payment-element';
      parentElement.appendChild(stripePaymentElement);
    }
    paymentElement.mount('#stripe-payment-element');
    paymentElement.on('change', e => {
      if (e.complete) {
        stripeMethodSelected = true;
        isChangedPaymentMethod = true;
      }
    });
    stripePaymentElementRendered = true;
  }

  export async function executePayment(confirmToken?: string, secureToken?: string) {
    if (stripeMethodSelected) {
      // There was a 3ds request from a previous purchase attempt
      if (confirmToken && secureToken) {
        const threeDSecureResultToken = await secureFlow(secureToken);
        return {
          billingInfo: {
            address: { country, postalCode },
            paymentGatewayReferences: [{ token: confirmToken, referenceType: 'stripe_confirmation_token' }],
            secureToken: threeDSecureResultToken.token
          }
        };
      }

      // Update the payment element mode based on the current state of isForSetup
      elements.update({ mode: isForSetup ? 'setup' : 'payment' });

      // This is just for display purposes, Recurly actually sets the charge price
      // based on our API request on the backend
      if (!isForSetup && (total ?? 0) > 0) {
        elements.update({ amount: Math.ceil((total ?? 0) * 100) });
      }
      const submitResponse = await elements.submit();
      if (submitResponse.error) {
        throw submitResponse.error;
      }
      // Handle the custom PayPal payment method
      if (submitResponse.selectedPaymentMethod === STRIPE_PAYPAL_PM) {
        return await new Promise((res, rej) => {
          // Initiate the Recurly PayPal flow
          window.recurly.configure(RECURLY_KEY);
          const paypal = window.recurly.PayPal({
            display: { displayName: 'Metafy' }
          });
          paypal.on('token', (token: { id: string }) => {
            res({
              billingInfo: {
                address: !!paymentMethod ? undefined : { country, postalCode },
                token: token.id
              }
            });
          });
          paypal.on('error', (err: Error) => {
            rej(err);
          });
          paypal.on('cancel', () => {
            rej(new Error('User cancelled the PayPal flow'));
          });
          paypal.start();
        });
      } else {
        // Otherwise, confirm and get a confirmation token
        const confirmResponse = await stripe.createConfirmationToken({
          elements,
          params: {
            return_url: `${BASE_APP_URL}/checkout/result`
          }
        });
        if (confirmResponse.error) {
          throw confirmResponse.error;
        }
        return {
          billingInfo: {
            address: { country, postalCode },
            paymentGatewayReferences: [
              { token: confirmResponse.confirmationToken.id, referenceType: 'stripe_confirmation_token' }
            ]
          }
        };
      }
    } else {
      // There was a 3ds request from a previous purchase attempt
      if (secureToken) {
        window.recurly.configure(RECURLY_KEY);
        const threeDSecureResultToken = await secureFlow(secureToken);
        return {
          billingInfo: {
            secureToken: threeDSecureResultToken.token
          }
        };
      }
      // We don't need to pass anything to the API when using a saved card
      return {};
    }
  }

  function getCardIcon(type: string) {
    switch (type) {
      case 'Visa':
        return faCcVisa;
      case 'MasterCard':
        return faCcMastercard;
      case 'American Express':
        return faCcAmex;
      case 'Discover':
        return faCcDiscover;
      case 'JCB':
        return faCcJcb;
      default:
        return faCreditCard;
    }
  }

  onMount(() => {
    getApolloClient()
      .query({
        query: gql`
          query GetCountries {
            countries
          }
        `
      })
      .then(response => {
        const { data } = response;
        listOfCountries = data.countries.map((c: string[]) => ({ label: c[1], value: c[0] }));
        listOfCountries.sort((a, b) => a.label.localeCompare(b.label));
      })
      .catch(error => captureException(error));

    const intervalId = setInterval(() => {
      if (!$cardsResponse?.loading) {
        clearInterval(intervalId);
        if (!paymentMethod) {
          if (!window.Stripe) {
            setTimeout(() => {
              renderStripePaymentElement();
            }, 1000);
          } else {
            renderStripePaymentElement();
          }
        }
      }
    }, 150);
  });
</script>

{#if $cardsResponse.loading}
  <div class="spinner large animate-pop-in-slow mt-8 h-[200px] w-full"></div>
{:else}
  <div bind:this={parentElement} class="flex flex-col space-y-4">
    {#if paymentMethod}
      <div
        class="bg-ondark-low flex flex-row items-center justify-between rounded-lg border px-3 py-3 shadow-[0px_2px_4px_rgba(0,_0,_0,_0.5),_0px_1px_6px_rgba(0,_0,_0,_0.25)]"
        class:border-[#282834]={stripePaymentElementRendered}
        class:border-action-ondark-base={savedMethodSelected}
      >
        <div
          class="flex items-center gap-x-2 font-bold"
          class:text-ondark-primary={!stripePaymentElementRendered}
          class:text-ondark-subtle={stripePaymentElementRendered}
        >
          {#if paymentMethod.type === 'paypal_billing_agreement'}
            <IconWrapper icon={faPaypal} size="xl" />
            <span>PayPal</span>
          {:else if paymentMethod.type === 'credit_card'}
            <IconWrapper icon={getCardIcon(paymentMethod.cardType)} size="xl" />
            <span>{paymentMethod.cardType}</span>
            <span>•••• {paymentMethod.lastFour}</span>
          {:else if paymentMethod.type === 'cash_app'}
            <svg class="h-6" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="16" height="16" rx="2.53521" fill="url(#cash-app-svg)"></rect>
              <path
                d="M8.40962 5.30142c.77002.00271 1.51444.27677 2.10238.774.061.05533.1408.08528.2231.08372.0823-.00156.1609-.03451.2197-.09212l.6-.6096c.0305-.03015.0543-.06628.0701-.1061.0158-.03982.0232-.08248.0218-.12531-.0015-.04282-.0118-.08487-.0303-.12352-.0185-.03865-.0448-.07306-.0772-.10107-.475-.40391-1.024-.71162-1.61639-.906l.18959-.8964c.0095-.04591.0086-.09336-.0026-.13889-.0112-.04553-.0324-.08799-.062-.1243-.0297-.03632-.06706-.06557-.10943-.08564-.04237-.02006-.08867-.03044-.13555-.03037H8.63161c-.07249-.00005-.14283.02467-.19936.07006-.05652.0454-.09584.10874-.11144.17954l-.17039.7968c-1.56.0768-2.88.8472-2.88 2.4264 0 1.3668 1.09079 1.9524 2.24399 2.358 1.0908.4056 1.66681.5556 1.66681 1.1268 0 .57118-.576.93118-1.4244.93118-.8237.013-1.61945-.2986-2.21521-.86758-.05938-.0579-.13905-.09031-.22199-.09031-.08295 0-.16261.03241-.222.09031l-.65161.63598c-.03037.0293-.05453.0645-.07103.1034-.0165.0388-.025.0806-.025.1228s.0085.084.025.1229c.0165.0389.04066.074.07103.1033.53018.5002 1.17829.8581 1.88401 1.0404l-.1752.834c-.00948.0464-.0087.0942.00229.1402.01098.046.0319.089.0613.1261.02939.0371.06653.0672.10882.0884.0423.0212.08871.0328.13599.0341h1.1796c.07296.0009.14398-.0235.20104-.069.05707-.0455.09668-.1093.11215-.1806l.1692-.7992c1.8636-.12 2.99999-1.1172 2.99999-2.58718 0-1.3512-1.13759-1.92-2.51999-2.388-.7884-.2856-1.47-.48-1.47-1.0668s.63721-.8004 1.27441-.8004Z"
                fill="#fff"
              >
              </path>
              <defs>
                <linearGradient id="cash-app-svg" x1="0" y1="8" x2="16" y2="8" gradientUnits="userSpaceOnUse">
                  <stop stop-color="#00C244"></stop><stop offset="1" stop-color="#00D64B"></stop>
                </linearGradient>
              </defs>
            </svg>
            <span>Cash App</span>
          {:else if paymentMethod.type === 'stripe_link'}
            <svg
              overflow="visible"
              viewBox="0 0 72 24"
              height="100%"
              xmlns="http://www.w3.org/2000/svg"
              role="presentation"
              focusable="false"
              fill="#fff"
              class="h-6 w-12"
            >
              <path
                d="M36.12 3.68a2.07 2.07 0 0 1 4.14 0c0 1.12-.92 2.07-2.07 2.07a2.06 2.06 0 0 1-2.07-2.07M29.98 1.92h3.6v20.16h-3.6zM40 7.68h-3.62v14.4H40zM66.1 14.39a12 12 0 0 0 5.32-6.71h-3.63a8.9 8.9 0 0 1-5.5 5V1.93h-3.63v20.16h3.63v-6c2.77.7 4.96 3.09 5.7 6h3.66c-.56-3.06-2.65-5.91-5.55-7.69M46.44 9.3a5.7 5.7 0 0 1 4.3-2 5 5 0 0 1 5.13 5.14v9.64h-3.63v-8.84c0-1.27-.56-2.74-2.4-2.74-2.16 0-3.4 1.92-3.4 4.16v7.42H42.8V7.7h3.63zM12 24a12 12 0 1 0 0-24 12 12 0 0 0 0 24"
              >
              </path>
              <path
                fill="#000"
                d="M11.45 4.8h-3.7A12 12 0 0 0 13.2 12a12 12 0 0 0-5.45 7.2h3.7a8.6 8.6 0 0 1 6.57-5.7v-3a8.5 8.5 0 0 1-6.57-5.7"
              >
              </path>
            </svg>
          {:else if paymentMethod.type === 'apple_pay'}
            <IconWrapper icon={faApplePay} size="xl" />
            <span>Apple Pay</span>
          {:else if paymentMethod.type === 'google_pay'}
            <IconWrapper icon={faGooglePay} size="xl" />
            <span>Google Pay</span>
          {:else}
            <IconWrapper icon={faCreditCard} size="xl" />
            <span>Your information on record</span>
          {/if}
        </div>
        <Button
          variant="ghost"
          on:click={() => {
            if (!savedMethodSelected) {
              stripeMethodSelected = false;
              stripePaymentElementRendered = false;
              isChangedPaymentMethod = false;

              country = $cardsResponse.data?.currentSession?.billingInfo?.country;
              postalCode = $cardsResponse.data?.currentSession?.billingInfo?.postalCode;
              savedMethodSelected = true;

              if (!paymentElement?._destroyed) {
                paymentElement?.unmount('#stripe-payment-element');
                paymentElement?.destroy();
              }
            } else {
              savedMethodSelected = false;
              renderStripePaymentElement();
            }
          }}
        >
          {#if savedMethodSelected}
            <span>Other payment methods</span>
          {:else}
            <span>Use this payment method</span>
          {/if}
        </Button>
      </div>
    {/if}
    {#if stripePaymentElementRendered}
      <!-- We need to know their high-level billing details -->
      <div class="flex flex-row space-x-4">
        <div class="select flex-1">
          <select
            class="bg-ondark-low h-12 w-full rounded-lg border-[#282834] outline-none"
            id="country-input"
            value={country}
            on:change={e => (country = e.target.value)}
          >
            <option label="Country" value="" disabled selected />
            {#each listOfCountries as c}
              <option label={c.label} value={c.value} />
            {/each}
          </select>
        </div>
        <div class="flex-1">
          <input
            id="postalCode-input"
            class="bg-ondark-low h-12 w-full rounded-lg border-[#282834] outline-none"
            placeholder="ZIP Code"
            type="text"
            value={postalCode}
            on:input={e => (postalCode = e.target.value)}
          />
        </div>
      </div>
      <SectionDivider />
    {/if}
    <div id="stripe-payment-element"></div>
  </div>

  {#if allowPayWithCredit}
    <div class="animate-pop-in-slow animate-delay-200 my-2">
      <TogglePayWithCredit
        id="{uid}-pay-with-credit"
        bind:payWithCredit
        message={payWithCreditMessage}
        tooltipContent={payWithCreditTooltipContent}
        on:toggle={({ detail }) => {
          dispatch('togglePayWithCredit', detail);
        }}
      />
    </div>
  {/if}
{/if}
